import React, { useEffect, useState } from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Chip, Tooltip
} from '@mui/material';
import { FileText } from 'lucide-react';
import * as actions from './actions';
import { toastError, toastSuccess } from '../../utils';
import StatusBadge from '../../components/StatusBadge';
import ActionButton from '../../components/ActionButton';
import ConfirmModal from '../Common/CommonModal';
import EmptyState from '../../components/EmptyState';
import moment from 'moment';

const leaveTypeLabels = {
  full_day: 'Full Day',
  half_day_morning: 'Morning',
  half_day_afternoon: 'Afternoon',
};

const cellSx = {
  color: '#CBD5E1',
  fontSize: '0.82rem',
  borderBottom: '1px solid #2E3247',
  py: 1.5,
};

const headCellSx = {
  color: '#94A3B8',
  fontSize: '0.75rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  borderBottom: '1px solid #2E3247',
  py: 1.2,
};

export default function MyLeaveHistory({ setLoader }) {
  const [leaves, setLeaves] = useState([]);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelLeaveId, setCancelLeaveId] = useState(null);

  useEffect(() => {
    loadMyLeaves();
  }, []);

  const loadMyLeaves = () => {
    setLoader(true);
    actions.getMyLeaves()
      .then((res) => {
        setLoader(false);
        setLeaves(res.body || []);
      })
      .catch((err) => {
        setLoader(false);
        toastError(err?.message || 'Failed to load leave history');
      });
  };

  const onCancelClick = (leaveId) => {
    setCancelLeaveId(leaveId);
    setCancelModalOpen(true);
  };

  const onConfirmCancel = () => {
    setCancelModalOpen(false);
    setLoader(true);
    actions.cancelLeave({ leaveId: cancelLeaveId })
      .then((res) => {
        setLoader(false);
        toastSuccess(res.message || 'Leave cancelled');
        loadMyLeaves();
      })
      .catch((err) => {
        setLoader(false);
        toastError(err?.message || 'Failed to cancel leave');
      });
  };

  return (
    <Box>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>Date</TableCell>
              <TableCell sx={headCellSx}>Type</TableCell>
              <TableCell sx={headCellSx}>Reason</TableCell>
              <TableCell sx={headCellSx}>Status</TableCell>
              <TableCell sx={headCellSx}>Review Note</TableCell>
              <TableCell sx={headCellSx}>Applied On</TableCell>
              <TableCell sx={headCellSx} align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {leaves.length === 0 ? (
              <EmptyState message="No leave history found" icon={FileText} colSpan={7} />
            ) : (
              leaves.map((leave) => (
                <TableRow key={leave.leaveId} sx={{ '&:hover': { backgroundColor: 'rgba(108, 99, 255, 0.04)' } }}>
                  <TableCell sx={cellSx}>
                    {moment(leave.leaveDate).format('DD MMM YYYY')}
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <Chip
                      label={leaveTypeLabels[leave.leaveType] || 'Full Day'}
                      size="small"
                      sx={{
                        backgroundColor: '#232636',
                        color: '#94A3B8',
                        fontSize: '0.73rem',
                        border: '1px solid #2E3247',
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ ...cellSx, maxWidth: 250 }}>
                    <Typography noWrap sx={{ fontSize: '0.82rem', color: '#CBD5E1' }}>
                      {leave.reason}
                    </Typography>
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <StatusBadge status={leave.status} />
                  </TableCell>
                  <TableCell sx={{ ...cellSx, maxWidth: 200 }}>
                    {leave.reviewNote ? (
                      <Tooltip
                        title={
                          <Box>
                            <Typography sx={{ fontSize: '0.78rem', color: '#F1F5F9', whiteSpace: 'pre-wrap' }}>
                              {leave.reviewNote}
                            </Typography>
                            {leave.reviewedBy && (
                              <Typography sx={{ fontSize: '0.68rem', color: '#94A3B8', mt: 0.5 }}>
                                — {leave.reviewedBy}
                              </Typography>
                            )}
                          </Box>
                        }
                        arrow
                        placement="top"
                        slotProps={{
                          tooltip: { sx: { backgroundColor: '#1A1D27', border: '1px solid #2E3247', borderRadius: '8px', px: 1.5, py: 1, maxWidth: 300 } },
                          arrow: { sx: { color: '#1A1D27', '&::before': { border: '1px solid #2E3247' } } },
                        }}
                      >
                        <Box>
                          <Typography noWrap sx={{ fontSize: '0.78rem', color: '#CBD5E1' }}>
                            {leave.reviewNote}
                          </Typography>
                          {leave.reviewedBy && (
                            <Typography noWrap sx={{ fontSize: '0.68rem', color: '#64748B', mt: 0.3 }}>
                              by {leave.reviewedBy}
                            </Typography>
                          )}
                        </Box>
                      </Tooltip>
                    ) : (
                      <Typography sx={{ fontSize: '0.78rem', color: '#4A5068' }}>—</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={cellSx}>
                    {moment(leave.createdAt).format('DD MMM YYYY')}
                  </TableCell>
                  <TableCell sx={cellSx} align="right">
                    {leave.status === 'pending' && (
                      <ActionButton
                        variant="danger"
                        onClick={() => onCancelClick(leave.leaveId)}
                        sx={{ fontSize: '0.75rem', px: 1.5, py: 0.3 }}
                      >
                        Cancel
                      </ActionButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ConfirmModal
        open={cancelModalOpen}
        title="Cancel Leave"
        message="Are you sure you want to cancel this leave application?"
        onSubmitConfirm={onConfirmCancel}
        handleClose={() => setCancelModalOpen(false)}
      />
    </Box>
  );
}
